import { GenerateImageOptions, GenerateImageRendererOptions } from "@centricsoftware/model-viewer";
import { red, white } from "designSystem/styles/color";
import { STANDARD_BORDER_STYLE } from "designSystem/styles/style-functions";
import { ComponentItem } from "designSystem/types/component-item-types";
import { isEqual, sortBy } from "lodash";

export const DEFAULT_BACKGROUND_COLOR = "#FFFFFFFF";

export enum MaterialSide {
	Front = "front",
	Back = "back",
}

export enum GeometryPrimitive {
	Cube = "MaterialPrimitiveCube",
	Sphere = "MaterialPrimitiveSphere",
	Fabric = "MaterialPrimitiveFabric",
}

export enum SnapshotTemplateId {
	Style = "StyleSnapshotTemplate",
	Material = "MaterialSnapshotTemplate",
}

export enum SnapshotMimeType {
	JPEG = "image/jpeg",
	PNG = "image/png",
}

export enum GenerateImageFormat {
	PNG = "PNG",
	JPEG = "JPEG",
}

export enum GenerateImageType {
	Image = "Image",
	Turntable = "Turntable",
}

export type TemplateOptions = {
	BackgroundColor: string;
	ImageFormat: string;
	ImageType: string;
	ImageRatio: string;
	ImageCount: string;
	ThreeDEnvironment: string;
	CameraPositions: string[];
	Use3DEnvironmentAsBackground: boolean;
	BaseGeometry?: string[];
	MaterialSide?: string[];
};

export interface SnapshotTemplateOptions {
	/** URL of environment on template node */
	environmentURL: string;

	/** Include all variants */
	includeAllVariants: boolean;

	/** Id of template if it exisit */
	selectedTemplateId: string;

	/** Style or Material types on template node*/
	productTypes: ComponentItem[];

	/** Template description */
	templateDescription: string;

	/** Template name */
	templateName: string;
}

export interface AutoPopulateOptions {
	/** Default Renderer Options to set */
	autoPopulatedRendererOptions?: GenerateImageRendererOptions;

	/** Default Image Options to set */
	autoPopulatedImageOptions?: GenerateImageOptions;

	/** Default Template Options to set */
	autoPopulatedTemplateOptions?: SnapshotTemplateOptions;
}

export const imageRatioMap = {
	"1:1": "square",
	"4:3": "portrait",
	"16:9": "landscape",
};

export const baseGeometryMap = {
	Cube: GeometryPrimitive.Cube,
	Sphere: GeometryPrimitive.Sphere,
	Fabric: GeometryPrimitive.Fabric,
};

export enum CameraView {
	Top = "Top",
	Bottom = "Bottom",
	Left = "Left",
	Right = "Right",
	Front = "Front",
	Back = "Back",
	Isometric = "Isometric",
}

/** All possible camera options to generate an image */
export const CAMERA_POSITIONS: ComponentItem[] = [
	{ id: CameraView.Top, res: "3DTopView" },
	{ id: CameraView.Bottom, res: "3DBottomView" },
	{ id: CameraView.Left, res: "3DLeftSideView" },
	{ id: CameraView.Right, res: "3DRightSideView" },
	{ id: CameraView.Front, res: "3DFrontView" },
	{ id: CameraView.Back, res: "3DBackView" },
	{ id: CameraView.Isometric, res: "3DIsometricView" },
];

/** Available Geometry Primitives for a Material */
export const MATERIAL_GEOMETRIES: ComponentItem[] = [
	{ id: GeometryPrimitive.Cube, name: "Cube", value: "BaseGeometry:Cube", res: "MaterialPrimitiveCube" },
	{ id: GeometryPrimitive.Sphere, name: "Sphere", value: "BaseGeometry:Sphere", res: "MaterialPrimitiveSphere" },
	{ id: GeometryPrimitive.Fabric, name: "Fabric", value: "BaseGeometry:Fabric", res: "MaterialPrimitiveFabric" },
];

/** Available Material Side to render */
export const MATERIAL_SIDES: ComponentItem[] = [
	{ id: MaterialSide.Front, name: "Front", value:"MaterialSide:Front", res: "Front" },
	{ id: MaterialSide.Back, name: "Back", value:"MaterialSide:Back", res: "Back" },
];

/** Function to return the res value from cameraPositions given a camera position name */
export const getCameraPositionRes = (cameraPosition: string): string => {
    	const cameraPositionItem = CAMERA_POSITIONS.find((item) => item.id === cameraPosition);
    	return cameraPositionItem?.res;
};

/** Function to sort arrays of template options before comparing two objects */
export const customTemplateOptionPositionSort = (objValue: TemplateOptions, othValue:TemplateOptions, key: string): boolean => {
	if (key === "CameraPositions") {
		return isEqual(sortBy(objValue), sortBy(othValue));
	}
	if (key === "BaseGeometry") {
		return isEqual(sortBy(objValue), sortBy(othValue));
	}
	if (key === "MaterialSide") {
		return isEqual(sortBy(objValue), sortBy(othValue));
	}
	// Fall back to default comparison for other properties
	return undefined;
};

/** Normalize exposure value for ModelViewer */
export const normalizeExposure = (value: number): number => {
	return value * 0.01;
};

/** Snapshot template styles for required labels */
export const REQUIRED_LABEL = {
	alignItems: "flex-start",
	"& .MuiFormControlLabel-asterisk": {
		display: "inline-block",
		color: red[600],
	},
};

/** Template Dialog Footer button styles */
export const FOOTER_BUTTONS = {
	position: "absolute",
	bottom: 0,
	left: "8px",
	width: "100%",
	borderTop: STANDARD_BORDER_STYLE,
	borderRadius: "0px 0px 3px 3px",
	backgroundColor: white[50],
	padding: "12px",
	paddingTop: "4px",
};

/** Template Dialog styles */
export const HIDE_OVERFLOW = {
	".MuiDialog-paperScrollPaper" : {
		overflowY: "hidden",
	},
};


/** Get the camera position options from a template definition */
export function cameraPositionOptionsFromTemplate(imageOptions: GenerateImageOptions, options: ComponentItem[]): ComponentItem[] {
	const { camera } = imageOptions;

	if (options.length === 0){
		return [];
	}

	const optionsMap = new Map(options.map((pos) => [pos.id, pos]));

	const cameraPos = camera.cameraPositions.map((item) => {
		const cameraPosition = optionsMap.get(item.id);
		return cameraPosition ? { ...cameraPosition } : null;
	}).filter((pos) => pos !== null); 
	
	return cameraPos;
}

/** Get the Material base geometry from a template definition */
export function materialPrimitivesFromTemplate(imageOptions: GenerateImageOptions): ComponentItem[] {
	const { materials } = imageOptions;
	if (!materials.primitives?.length){
		return [];
	}

	const geometriesMap = new Map(MATERIAL_GEOMETRIES.map((prim) => [prim.id, prim]));
	const primitives = materials.primitives.map((item) => {
	  const primitive = geometriesMap.get(item);
	  return primitive ? { ...primitive } : null;
	}).filter((prim) => prim !== null);

	return primitives;
}

/** Get the Material side from a template definition */
export function materialSidesFromTemplate(imageOptions: GenerateImageOptions): ComponentItem[] {
	const { materials } = imageOptions;
	if (!materials.sides?.length){
		return [];
	}
	const sidesMap = new Map(MATERIAL_SIDES.map((side) => [side.id, side]));
	const sides = materials.sides.map((item) => {
		const side = sidesMap.get(item);
		return side ? { ...side } : null;
	}).filter((side) => side !== null);

	return sides;
}